import { Dayjs } from "dayjs";
import { DetailedUser } from "@/user/user";
import { IActiveAiScriptRun } from "./ai-script/types";
import { PAYROLL_RUN_STATUS } from "./constants";

// Backend models (matching API serializers)
export interface IPayPeriod {
  id: string;
  start_date: string; // "YYYY-MM-DD"
  end_date: string;
  status: "pending" | "marked_as_paid";
  created_at: string;
  updated_at: string;
  runs_count: number;
  latest_run_at: string | null;
  latest_run_status: PayrollRunStatus | null;
  icm_runs_count: number;
  latest_icm_run_id: string | null;
  latest_icm_run_status: PayrollRunStatus | null;
  latest_icm_run_at: string | null;
  latest_completed_icm_run_id: string | null;
  latest_completed_icm_run_plan_id: string | null;
  latest_completed_icm_run_plan_name: string | null;
  latest_completed_ai_script_run_id: string | null;
  latest_ai_script_run_status: string | null;
  latest_ai_script_run_at: string | null;
  latest_completed_ai_script_run_generated_workbook_url: string | null;
  latest_completed_ai_script_run_generated_workbook_metadata: IPayrollRunFileMetadata | null;
  // Lightweight pointers for the read-only Roster tab: the roster version this
  // period's shown run ran against, and the business's live published version
  // (for the "this run used v22 · live is now v27" hint). The heavy fields +
  // members are loaded lazily via GET payroll_roster?version=N. Null when the
  // run predates roster snapshots / the business has no roster.
  roster_version_number: number | null;
  current_roster_version_number: number | null;
  // Analogous pointers for the AI SCRIPT version: the version this period's
  // shown run ran against, and the business's live published version. Drive the
  // re-run dialog's "reproduce (script v3 · roster v22)" vs "latest (script v5 ·
  // roster v27)" drift callouts. Null when the period has no completed run / no
  // published version.
  ai_script_version_number: number | null;
  current_ai_script_version_number: number | null;
  // The exact ids the run used, sent back to REPRODUCE it. Running with "latest"
  // omits both so the backend defaults to the published version + roster.
  latest_completed_ai_script_run_version_id: string | null;
  latest_completed_ai_script_run_roster_id: string | null;
  // The period's run queue, oldest first: the run executing plus everything
  // waiting behind it, each with the tabs it rewrites (`sheet_scope` null = the
  // whole workbook). Runs execute one at a time per period, so this drives
  // active-run indicators and rerun availability.
  active_ai_script_runs?: IActiveAiScriptRun[];
}

/**
 * The two sign-offs a unit can carry. Read as a progression: a department is
 * cleared for its technicians to approve their own pay, and then payroll itself
 * is approved.
 *
 * Defined once and shared with the request-approval feature — both surfaces read
 * the same table, and two vocabularies for one enum is how they drift.
 */
export const SHEET_APPROVAL_TYPE = {
  readyForTechApproval: "ready_for_tech_approval",
  payrollFinalized: "payroll_finalized",
} as const;

export type SheetApprovalType =
  (typeof SHEET_APPROVAL_TYPE)[keyof typeof SHEET_APPROVAL_TYPE];

/**
 * One gate's current state, and who settled it.
 *
 * The provenance is what an append-only log is for: `approved: false` alone
 * cannot tell a department nobody has signed off from one the system withdrew
 * when a rerun moved its numbers, and only the second needs acting on.
 */
export interface ISheetApprovalStatus {
  approved: boolean;
  at: string | null;
  actor_name: string | null;
  source: "user" | "system" | null;
  reason: string | null;
}

export interface IPayrollSheetApprovalAccessKey {
  /** Access key id — what an approval action is posted against. */
  id: string;
  /** Access key string — what a sheet carries, so tabs can be matched to units. */
  key: string;
  name: string;
  sheet_names: string[];
  /** Whether THIS reader may set these approvals, or only see them. */
  approval_authorized: boolean;
  approvals: Record<SheetApprovalType, ISheetApprovalStatus>;
}

/**
 * Counts are deliberately absent: `access_keys` is already the
 * requester-filtered list, so a total is a `filter().length` away and a second
 * copy on the wire is only a chance for the two to disagree.
 */
export interface IPayrollSheetApprovalStatus {
  access_keys: IPayrollSheetApprovalAccessKey[];
}

export interface IPayrollSheetApprovalAction {
  id: string;
  access_key_id: string;
  approval_type: SheetApprovalType;
  enabled: boolean;
  reason: string | null;
  actor_id: string;
  originating_ai_script_run_id: string;
  content_fingerprint: string;
  created_at: string;
}

export interface IPayPeriodDetail extends IPayPeriod {
  payroll_runs: IPayrollRun[];
  // Sheet approval state is requester-filtered and needs a fingerprint pass over
  // the workbook, so it is computed on the SHOW endpoint only — the periods
  // index deliberately does not carry it. Read it from the period detail query.
  approval_status?: IPayrollSheetApprovalStatus;
}

export type PayrollRunStatus =
  (typeof PAYROLL_RUN_STATUS)[keyof typeof PAYROLL_RUN_STATUS];

export interface IPayrollRunFileMetadata {
  filename: string;
  content_type: string;
  size: number;
}

export interface IPayrollRun {
  id: string;
  status: PayrollRunStatus;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  result_file_url: string | null;
  result_file_metadata: IPayrollRunFileMetadata | null;
  is_manual: boolean;
  user: DetailedUser | null;
  user_display_name: string;
}

// Client-side enriched types
export interface EnrichedPayPeriod extends IPayPeriod {
  key: string;
  start: Dayjs;
  end: Dayjs;
  displayRange: string; // "Dec 1 - Dec 7"
  periodStatus: "past" | "current" | "future";
  existsInDb: boolean; // false for calculated current period
}
